import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { YoutubeEmbed } from "@/components/ClipCard";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowUp, ArrowLeft, MessageCircle, Trophy, Flame } from "lucide-react";
import { motion } from "framer-motion";

export default function ClipDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [clip, setClip] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [upvoted, setUpvoted] = useState(false);

  useEffect(() => {
    api.get(`/clips/${id}`).then(({ data }) => setClip(data));
    api.get(`/clips/${id}/comments`).then(({ data }) => setComments(data));
    if (user) api.get(`/clips/${id}/vote_status`).then(({ data }) => setUpvoted(data.upvoted)).catch(() => {});
  }, [id, user]);

  const upvote = async () => {
    if (!user) return toast.error("Sign in to vote");
    const { data } = await api.post(`/clips/${id}/upvote`);
    setClip((c) => ({ ...c, upvotes: data.upvotes }));
    setUpvoted(data.upvoted);
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!user) return toast.error("Sign in to comment");
    setPosting(true);
    try {
      const { data } = await api.post(`/clips/${id}/comments`, { text });
      setComments((c) => [data, ...c]);
      setText("");
    } finally {
      setPosting(false);
    }
  };

  if (!clip) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 pb-24 md:pb-12">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> back to leaderboard
      </Link>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-6">
        <YoutubeEmbed youtubeId={clip.youtube_id} title={clip.title} />

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest ${
                clip.category === "standup" ? "border-amber-400/40 bg-amber-400/10 text-amber-400" : "border-[#FF2E55]/40 bg-[#FF2E55]/10 text-[#FF2E55]"
              }`}>
                {clip.category === "standup" ? "STAND-UP" : "SKIT"}
              </span>
              <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest text-slate-400">{clip.era}</span>
              <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest text-slate-400">{clip.style?.replace("_", " ")}</span>
            </div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl text-white leading-none">{clip.title}</h1>
            <p className="mt-2 text-slate-300">
              <span className="text-[#00F0FF]">◆</span> {clip.comedian}
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <Button
              onClick={upvote}
              data-testid="detail-upvote-btn"
              className={`btn-brutal font-bold ${upvoted ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-white/5 text-white border border-white/10 hover:bg-white/10"}`}
            >
              <ArrowUp className="mr-2 h-4 w-4" /> {clip.upvotes}
            </Button>
            <div className="flex items-center gap-3 text-xs font-mono-ui text-slate-400">
              <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5 text-amber-400" /> ELO {clip.elo}</span>
              <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5 text-[#FF2E55]" /> {clip.wins}W/{clip.losses}L</span>
            </div>
          </div>
        </div>

        {clip.description && (
          <p className="text-slate-300 leading-relaxed border-l-2 border-amber-400/50 pl-4">{clip.description}</p>
        )}

        <p className="text-xs font-mono-ui uppercase tracking-widest text-slate-500">
          Submitted by {clip.submitted_by_name}
        </p>

        <hr className="border-white/5" />

        {/* Comments */}
        <div>
          <div className="mb-4 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-amber-400" />
            <h2 className="font-display text-2xl text-white">{comments.length} REACTIONS</h2>
          </div>

          {user ? (
            <form onSubmit={postComment} className="mb-6">
              <Textarea
                data-testid="comment-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Was it funny or nah? Roast responsibly..."
                className="bg-black/40 border-white/10 focus:border-amber-400"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  data-testid="post-comment-btn"
                  type="submit"
                  disabled={posting || !text.trim()}
                  className="bg-amber-400 text-black hover:bg-amber-300 font-bold btn-brutal"
                >
                  Post
                </Button>
              </div>
            </form>
          ) : (
            <p className="mb-6 text-sm text-slate-400">
              <Link to="/login" className="text-amber-400 underline">Sign in</Link> to comment
            </p>
          )}

          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} data-testid={`comment-${c.id}`} className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-sm font-semibold text-amber-400">{c.user_name}</span>
                  <span className="text-[10px] font-mono-ui uppercase tracking-widest text-slate-500">
                    {new Date(c.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-slate-200 whitespace-pre-wrap">{c.text}</p>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-sm text-slate-500 italic">No reactions yet. Break the silence.</p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
